import React from 'react';
import { Star, Trash2, Clock, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных часовых поясов
 */
const SavedTimeZonesSidebar = ({ 
  savedTimeZones, 
  deleteSavedTimeZone, 
  loadSavedTimeZone 
}) => {
  // Если нет сохраненных часовых поясов
  if (!savedTimeZones || savedTimeZones.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          Сохраненные настройки
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные вами часовые пояса и настройки. Используйте функцию сохранения, чтобы быстро возвращаться к нужным городам.
        </p>
      </div>
    );
  }

  // Функция форматирования даты сохранения
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          Сохраненные настройки
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedTimeZones.length} {getCountSuffix(savedTimeZones.length)}
        </span>
      </div>

      <div className="space-y-4">
        {savedTimeZones.map((timeZone) => (
          <div 
            key={timeZone.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {timeZone.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => deleteSavedTimeZone(timeZone.id)}
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  title="Удалить"
                />
              </div>
            </div>
            
            <div className="mt-2 text-sm">
              <div className="flex items-center text-gray-600 dark:text-gray-400 mb-1.5">
                <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                <span className="text-xs">
                  Сохранено: {formatDate(timeZone.date)}
                </span>
              </div>
              
              {timeZone.formData.view === 'converter' && (
                <div className="flex items-center justify-between mt-2">
                  <div className="text-gray-700 dark:text-gray-300">
                    <div className="font-medium">{timeZone.sourceCity?.name || timeZone.formData.sourceCity}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      UTC{timeZone.sourceCity?.utcOffset || ''}
                    </div>
                  </div>
                  
                  <ArrowRight className="w-4 h-4 text-gray-400 mx-2" />
                  
                  <div className="text-gray-700 dark:text-gray-300 text-right">
                    <div className="font-medium">{timeZone.targetCity?.name || timeZone.formData.targetCity}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      UTC{timeZone.targetCity?.utcOffset || ''}
                    </div>
                  </div>
                </div>
              )}
              
              {timeZone.formData.view === 'worldclock' && (
                <div className="text-gray-700 dark:text-gray-300 mt-1">
                  <span className="font-medium">Мировые часы</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    Просмотр нескольких часовых поясов
                  </span>
                </div>
              )}
              
              {timeZone.formData.view === 'meeting' && (
                <div className="text-gray-700 dark:text-gray-300 mt-1">
                  <span className="font-medium">Планировщик встреч</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    Встреча для участников из разных городов
                  </span>
                </div>
              )}
              
              {timeZone.formData.view === 'difference' && (
                <div className="text-gray-700 dark:text-gray-300 mt-1">
                  <span className="font-medium">Разница во времени</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    Между {timeZone.sourceCity?.name || timeZone.formData.sourceCity} и {timeZone.targetCity?.name || timeZone.formData.targetCity}
                  </span>
                </div>
              )}
            </div>
            
            <button
              onClick={() => loadSavedTimeZone(timeZone.id)}
              className="mt-3 w-full py-1.5 px-3 text-xs text-center bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-800/50 text-purple-700 dark:text-purple-300 rounded transition-colors"
            >
              Загрузить настройки
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "элемент" в зависимости от числа
const getCountSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'элементов';
  }
  
  if (lastDigit === 1) {
    return 'элемент';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'элемента';
  }
  
  return 'элементов';
};

export default SavedTimeZonesSidebar;